import { FC } from 'react';
import NumberFormat from 'react-number-format';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import { useTranslate } from 'hooks/useTranslate';

import { numberFormat } from 'constants/formats';

import UploadDocument from 'modules/documents/components/UploadDocument';
import InputTextArea from 'modules/_shared/components/InputTextArea';
import NotarySection from 'modules/_shared/components/NotarySection';
import InputDate from 'modules/_shared/components/InputDate';
import CustomLoading from 'modules/_shared/components/CustomLoading';

import { useConstitutionModal } from '../context/ConstitutionModalContext';

const TYPE_FILES_ALLOWED = '.xls,.xlsx';

const ConstitutionModalScriptureView: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useConstitutionModal();

  return (
    <>
      <div className="row g-4 mb-4">
        <div className="col-12 col-lg-4">
          <InputDate
            id="date"
            label={t('Date')}
            value={data.date || ''}
            onChange={actions.handleChangeDate}
            minDate={data.lastOperationDate}
            errorMessageKey="InvalidOperationDate"
            required
            lgSize
          />
        </div>

        <div className="col-12 col-lg-4">
          <div className="form-group">
            <label className="form-label" htmlFor="total-shares">
              {t('NºNewParticipations')} <MandatoryField />
            </label>

            <div className="form-control-wrap">
              <div className="form-text-hint">
                <span className="overline-title">{t('Nr')}</span>
              </div>
              <NumberFormat
                id="total-shares"
                className="form-control form-control-lg"
                value={data.totalSharesToAdd}
                onChange={actions.handleChangeTotalSharesToAdd}
                thousandSeparator={numberFormat.thousandSeparator}
                decimalSeparator={numberFormat.decimalSeparator}
                allowNegative={false}
                placeholder={t('NumberOfNewParticipations')}
                decimalScale={0}
                required
                disabled={data.isExcelMode}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="form-group">
            <label className="form-label" htmlFor="nominal-value">
              {t('NominalValue')} <MandatoryField />
            </label>

            <div className="form-control-wrap">
              <div className="form-text-hint">
                <span className="overline-title">Eur</span>
              </div>
              <NumberFormat
                id="nominal-value"
                className="form-control form-control-lg"
                value={data.nominalValue}
                onChange={actions.handleChangeNominalValue}
                thousandSeparator={numberFormat.thousandSeparator}
                decimalSeparator={numberFormat.decimalSeparator}
                allowNegative={false}
                placeholder={t('NominalValueOfParticipati')}
                decimalScale={10}
                required
              />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="form-group card card-bordered constitution-modal__excel-card">
            <div className="card-inner">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex">
                  <em className="icon ni ni-file-xls text-primary mr-2 mt-1" />
                  <div>
                    <label className="form-label mb-1">
                      {t('ConstitutionExcelQuickPath')}
                    </label>
                    <p className="text-soft small mb-0">
                      {t('ConstitutionExcelQuickPathDescription')}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={actions.handleDownloadExcelTemplate}
                  data-testid="download-constitution-excel-template"
                >
                  <em className="icon ni ni-download" />
                  <span className="ml-1">
                    {t('DownloadConstitutionExcelTemplate')}
                  </span>
                </button>
              </div>

              <UploadDocument
                inputLabel={
                  data.excelFile?.name || t('DragConstitutionExcelToUpload')
                }
                file={data.excelFile}
                setFile={actions.handleChangeExcelFile}
                accept={TYPE_FILES_ALLOWED}
                skipFileValidation
                idIndex={1}
              />

              {data.isParsingExcel && <CustomLoading />}
            </div>
          </div>
        </div>

        <div className="col-12">
          <UploadDocument
            label="AddDocumentOptional"
            inputLabel={data.file?.name || t('DragDocumentToUpload')}
            file={data.file}
            setFile={actions.handleChangeFile}
          />
        </div>

        <div className="col-12">
          <InputTextArea
            id="comments"
            label={t('CommentsLabel')}
            value={data.comments}
            onChange={actions.handleChangeComments}
            noResize
          />
        </div>
      </div>

      <div className="row mb-4">
        <NotarySection
          notaryName={data.notaryName}
          onChangeName={actions.handleChangeNotaryName}
          notaryProtocol={data.notaryProtocol}
          onChangeProtocol={actions.handleChangeNotaryProtocol}
          notaryCity={data.notaryCity}
          onChangeCity={actions.handleChangeNotaryCity}
          lgSize
        />
      </div>
    </>
  );
};

export default ConstitutionModalScriptureView;
